// -----------------------------------------------------------
// FloraIQ - LadmModelingStagePanel (Aba Etapa 5 - Modelagem LADM)
// -----------------------------------------------------------

import { ladmCompleteness, ladmModelingData } from "@/mocks/operational";

const statusColors: Record<string, string> = {
  "Completo": "bg-green-100 text-green-700",
  "Incompleto": "bg-orange-100 text-orange-700",
  "Crítico": "bg-red-100 text-red-700",
  "Pendente": "bg-yellow-100 text-yellow-700",
};

export default function LadmModelingStagePanel() {
  const items = [
    { label: "Unidade Espacial", value: ladmCompleteness.arvoresComUnidadeEspacial, total: ladmCompleteness.totalArvores },
    { label: "Fonte Documental", value: ladmCompleteness.arvoresComFonte, total: ladmCompleteness.totalArvores },
    { label: "Responsável", value: ladmCompleteness.arvoresComResponsavel, total: ladmCompleteness.totalArvores },
    { label: "RRR", value: ladmCompleteness.arvoresComRRR, total: ladmCompleteness.totalArvores },
    { label: "Histórico", value: ladmCompleteness.arvoresComHistorico, total: ladmCompleteness.totalArvores },
    { label: "Documentos", value: ladmCompleteness.arvoresComDocumentos, total: ladmCompleteness.totalArvores },
    { label: "Nuvem de Pontos", value: ladmCompleteness.arvoresComNuvemPontos, total: ladmCompleteness.totalArvores },
  ];

  return (
    <div className="space-y-5">
      {/* Completude LADM */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((item) => {
          const pct = Math.round((item.value / item.total) * 100);
          return (
            <div key={item.label} className="rounded-card border border-background-200/70 p-3 text-center">
              <p className="text-xs text-foreground-500">{item.label}</p>
              <p className="text-xl font-bold text-foreground-900 font-heading mt-1">{item.value.toLocaleString("pt-BR")}</p>
              <div className="w-full bg-background-100 rounded-full h-1.5 mt-2">
                <div className="bg-primary-500 h-1.5 rounded-full" style={{ width: `${pct}%` }}></div>
              </div>
              <p className="text-[10px] text-foreground-400 mt-1">{pct}% do total</p>
            </div>
          );
        })}
      </div>

      {/* Matriz visual */}
      <div className="rounded-card border border-background-200/70 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-4">Matriz Visual LADM-FloraIQ</h4>
        <div className="hidden md:flex items-center gap-2">
          {[
            { label: "Party", color: "bg-blue-500", desc: "Partes" },
            { label: "→", color: "", desc: "" },
            { label: "RRR", color: "bg-orange-500", desc: "Restrições" },
            { label: "→", color: "", desc: "" },
            { label: "Spatial Unit", color: "bg-green-500", desc: "Unidade Espacial" },
            { label: "→", color: "", desc: "" },
            { label: "Source", color: "bg-purple-500", desc: "Fontes" },
            { label: "→", color: "", desc: "" },
            { label: "Histórico", color: "bg-teal-500", desc: "Versionamento" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center">
              {item.color ? (
                <div className={`${item.color} text-white text-xs font-bold px-3 py-2 rounded-lg text-center min-w-[100px]`}>
                  <p>{item.label}</p>
                  <p className="text-[9px] opacity-80">{item.desc}</p>
                </div>
              ) : (
                <span className="text-foreground-400 text-lg font-bold px-1">→</span>
              )}
            </div>
          ))}
        </div>
        <div className="md:hidden space-y-2">
          {["Party", "RRR", "Spatial Unit", "Source", "Histórico"].map((label, idx) => (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${["bg-blue-500", "bg-orange-500", "bg-green-500", "bg-purple-500", "bg-teal-500"][idx]}`}></div>
              <span className="text-xs text-foreground-700">{label}</span>
              {idx < 4 && <i className="ri-arrow-down-line text-foreground-300"></i>}
            </div>
          ))}
        </div>
      </div>

      {/* Tabela de vínculos */}
      <div className="rounded-card border border-background-200/70 p-4">
        <h4 className="text-sm font-bold text-foreground-900 mb-3">Vínculos LADM por Árvore</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/50">
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Código CTA</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Unidade Espacial</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Fonte</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Responsável</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">RRR</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Histórico</th>
                <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {ladmModelingData.map((row) => (
                <tr key={row.codigoCTA} className="border-b border-background-100/70 hover:bg-background-50/50">
                  <td className="px-2 py-2 text-xs font-medium text-foreground-800">{row.codigoCTA}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{row.unidadeEspacial}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{row.fonte}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{row.responsavel}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{row.rrr}</td>
                  <td className="px-2 py-2 text-xs text-foreground-600">{row.historico}</td>
                  <td className="px-2 py-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[row.status] || ""}`}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}